import { useEffect, useState } from 'react'
import { ferry, money, clock, type OpsDashboard, type TodaySailing } from '../lib/ferry'
import { t } from '../lib/i18n'

/**
 * The operator dashboard.
 *
 * ── What this screen is for ──────────────────────────────────────────────────
 *
 * This is the screen that decides whether a ferry company adopts the app, so it
 * is built around one honest claim rather than a wall of charts: scanning a
 * party instead of counting one saves measurable minutes at the gate, and here
 * is the arithmetic with its assumptions printed underneath.
 *
 * Two rules held throughout:
 *
 *  1. Every number that is derived states what it was derived FROM. A dashboard
 *     that says "saved 41 minutes" without saying how is a dashboard whose first
 *     challenge from an operations manager it cannot survive — and they will
 *     challenge it, correctly, in the first meeting.
 *
 *  2. "Where to look" is the only section that tells anyone what to do, and it
 *     is generated from thresholds rather than assembled by hand, so it goes
 *     quiet when nothing is wrong. A permanent list of advice is wallpaper.
 */
export default function Ops() {
  const [days, setDays] = useState(30)
  const [d, setD] = useState<OpsDashboard | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    ferry.ops.dashboard(days).then(setD).catch((e: Error) => setError(e.message))
  }, [days])

  if (error) return <section className="card"><p className="error">{error}</p></section>
  if (!d) return <section className="card"><p className="muted">Loading…</p></section>

  return (
    <section className="card ops">
      <header className="ops-head">
        <h1>Operations</h1>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </header>

      {d.whereToLook.length > 0 && (
        <div className="advice">
          <h2>Where to look</h2>
          {d.whereToLook.map((a, i) => (
            <div key={i} className="advice-item">
              <strong>{a.headline}</strong>
              <p className="muted">{a.detail}</p>
            </div>
          ))}
        </div>
      )}

      <div className="tiles">
        <Tile label="Boarded" value={d.gate.peopleBoarded.toLocaleString()} sub={`${d.gate.scans.toLocaleString()} scans`} />
        <Tile label="People per scan" value={d.gate.peoplePerScan.toFixed(1)}
          sub="each one a queue interaction that did not happen" />
        <Tile label="Gate time saved" value={`${Math.round(d.gate.minutesSaved)} min`} sub="see the assumption below" emphasis />
        <Tile label="Load factor" value={`${d.load.loadFactorPct}%`} sub={`${d.load.emptySeats.toLocaleString()} empty seats`} />
        <Tile label="Revenue" value={money(d.revenue.gross / 100)} sub={`${d.revenue.passengers.toLocaleString()} passengers`} />
        <Tile label="Resident share" value={`${d.revenue.residentShare}%`} sub="of passengers, not of revenue" />
      </div>

      {/*
       * The assumptions, printed. This is the paragraph that makes the number
       * above it worth anything — an operator who can see the inputs can argue
       * with them, and an operator who can argue with them believes the output.
       */}
      <p className="assumption muted small">
        Gate time saved assumes {d.gate.assumptions.secondsPerPersonManual}s to check one person
        by hand against {d.gate.assumptions.secondsPerScan}s to scan a whole party.
        {' '}{d.gate.assumptions.note} Change these to your own timings and the figure moves with them.
      </p>

      <h2>Punctuality</h2>
      <table className="grid">
        <thead><tr><th>Route</th><th>Sailings</th><th>On time</th><th>Avg delay</th><th>Cancelled</th></tr></thead>
        <tbody>
          {d.punctuality.map((r) => (
            <tr key={r.route}>
              <td>{r.route.split('-').join(' → ')}</td>
              <td>{r.sailings}</td>
              <td className={r.onTimePct != null && r.onTimePct < 70 ? 'bad' : ''}>
                {r.onTimePct == null ? '—' : `${r.onTimePct}%`}
              </td>
              <td>{r.avgDelayMinutes ? `${r.avgDelayMinutes} min` : '—'}</td>
              <td>{r.cancelled || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>When the boats are full</h2>
      {/*
       * By hour rather than by day, because the decision this informs is which
       * sailing to add — and that is an hour-of-day question. A daily total
       * averages a 95%-full 7am and a 20%-full 2pm into a number that suggests
       * doing nothing.
       */}
      <div className="hours">
        {d.load.byHour.map((h) => (
          <div key={h.hour} className="hour" title={`${h.booked} of ${h.seats} seats`}>
            <div className="bar" style={{ height: `${Math.max(3, h.loadPct)}%` }} data-full={h.loadPct >= 90} />
            <span className="muted small">{h.hour}</span>
          </div>
        ))}
      </div>

      <h2>Money that did not arrive</h2>
      <div className="tiles">
        {/*
         * Deliberately two numbers. A paid seat nobody sat in is revenue kept and
         * capacity wasted; an abandoned hold is capacity held back from somebody
         * who would have paid. They point at opposite fixes and averaging them
         * hides both.
         */}
        <Tile label="Paid, never boarded" value={d.leakage.paidNeverBoarded.toLocaleString()}
          sub="revenue kept, seat wasted" />
        <Tile label="Abandoned holds" value={d.leakage.abandonedHolds.toLocaleString()}
          sub="seats withheld from a buyer" />
        <Tile label="Refused at the gate" value={d.gate.refused.toLocaleString()}
          sub={d.gate.refusedBreakdown.map((r) => `${r.n} ${r.outcome.split('_').join(' ')}`).join(' · ') || 'none'} />
      </div>

      <p className="muted small">
        {d.window.from} to {d.window.to}.
      </p>
    </section>
  )
}

function Tile({ label, value, sub, emphasis }: { label: string; value: string; sub?: string; emphasis?: boolean }) {
  return (
    <div className={`tile${emphasis ? ' tile-emphasis' : ''}`}>
      <span className="tile-label muted small">{label}</span>
      <strong className="tile-value">{value}</strong>
      {sub && <span className="tile-sub muted small">{sub}</span>}
    </div>
  )
}

/**
 * The live board: what is happening right now, for the person on shift.
 *
 * The rows are TYPED. They were `Record<string, unknown>`, and the boarded
 * column rendered `NaN` for a week — the API returns `aboard`, this read
 * `boarded`, and an index signature made both look equally valid to the
 * compiler. `Number(undefined)` is NaN and React renders it happily.
 */
export function Today() {
  const [rows, setRows] = useState<TodaySailing[] | null>(null)
  useEffect(() => {
    const tick = () => { void ferry.ops.today().then((d) => setRows(d.sailings)).catch(() => setRows([])) }
    tick()
    const timer = window.setInterval(tick, 60_000)
    return () => window.clearInterval(timer)
  }, [])

  if (rows === null) return <p className="muted">{t('app.loading')}</p>
  if (rows.length === 0) return <p className="muted">{t('ops.nothingToday')}</p>

  return (
    <table className="grid">
      <thead>
        <tr>
          <th>{t('ops.departs')}</th><th>{t('ops.vessel')}</th>
          <th>{t('ops.booked')}</th><th>{t('ops.aboard')}</th>
          <th>{t('ops.stillToBoard')}</th><th>{t('ops.status')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{clock(r.scheduled_departure)}</td>
            <td>{r.vessel_name}</td>
            <td>{num(r.booked)}</td>
            <td>{num(r.aboard)}</td>
            {/* The number the dock actually runs on: who is still expected. */}
            <td><strong>{num(r.stillToBoard)}</strong></td>
            <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * A number, or a dash.
 *
 * Belt and braces alongside the types: if a field ever goes missing again, a
 * dash says "we do not know" and NaN says nothing at all. On a board somebody
 * runs a dock from, the difference matters — a dash sends them to ask, and NaN
 * makes them distrust the whole screen.
 */
function num(v: number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString() : '—'
}
